/**
 * Service for managing JWT tokens in the VV Health application
 * This service provides functions for generating, verifying, and storing JWT tokens
 */

// Constants for JWT configuration
const TOKEN_STORAGE_KEY = 'vvhealth_auth_token';
const TOKEN_EXPIRY = 7 * 24 * 60 * 60 * 1000; // 7 days in milliseconds
const MAGIC_LINK_EXPIRY = 48 * 60 * 60 * 1000; // 48 hours in milliseconds

/**
 * Simple base64 encoding function (for browser compatibility)
 * @param {String} str - String to encode
 * @returns {String} Base64 encoded string
 */
const base64Encode = (str) => {
  try {
    return btoa(encodeURIComponent(str).replace(/%([0-9A-F]{2})/g, (match, p1) => {
      return String.fromCharCode('0x' + p1);
    }));
  } catch (e) {
    console.error('Error encoding to base64:', e);
    return '';
  }
};

/**
 * Simple base64 decoding function (for browser compatibility)
 * @param {String} str - Base64 string to decode
 * @returns {String} Decoded string
 */
const base64Decode = (str) => {
  try {
    return decodeURIComponent(atob(str).split('').map(function(c) {
      return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
    }).join(''));
  } catch (e) {
    console.error('Error decoding from base64:', e);
    return '';
  }
};

/**
 * Creates a token payload for a user
 * @param {Object} user - The Parse user object
 * @returns {Object} Token payload
 */
export const createTokenPayload = (user) => {
  if (!user) {
    throw new Error('A user is required to create a token payload');
  }

  return {
    id: user.id,
    email: user.get('email'),
    userType: user.get('userType') || 'client',
  };
};

/**
 * Generates a simple token for a user (browser-compatible)
 * @param {Object} user - The Parse user object
 * @param {Object} options - Additional options for token generation
 * @param {boolean} options.isMagicLink - Whether this token is for a magic link
 * @returns {String} Generated token
 */
export const generateToken = (user, options = {}) => {
  if (!user) {
    throw new Error('A user is required to generate a token');
  }
  
  const payload = createTokenPayload(user);
  const now = Date.now();
  
  // Determine expiration time based on token type
  const expiry = options.isMagicLink ? MAGIC_LINK_EXPIRY : TOKEN_EXPIRY;
  
  // Create a token with expiration time
  const tokenData = {
    ...payload,
    iat: now,
    exp: now + expiry,
    isMagicLink: options.isMagicLink || false
  };
  
  // Simple token format: base64(JSON payload)
  return base64Encode(JSON.stringify(tokenData));
};

/**
 * Generates a magic link token specifically for quotation access
 * @param {Object} user - The Parse user object or user data
 * @returns {String} Generated magic link token
 */
export const generateMagicLinkToken = (user) => {
  return generateToken(user, { isMagicLink: true });
};

/**
 * Decodes a token
 * @param {String} token - Token to decode
 * @returns {Object|null} Decoded payload or null if invalid
 */
export const decodeToken = (token) => {
  if (!token) return null;

  try {
    const decoded = JSON.parse(base64Decode(token));
    return decoded;
  } catch (error) {
    console.error('Error decoding token:', error);
    return null;
  }
};

/**
 * Verifies a token
 * @param {String} token - Token to verify
 * @returns {Object|null} Decoded payload or null if invalid/expired
 */
export const verifyToken = (token) => {
  if (!token) return null;

  try {
    const decoded = decodeToken(token);
    
    // Check if token is expired
    if (decoded && decoded.exp) {
      const currentTime = Date.now();
      if (decoded.exp < currentTime) {
        console.warn('Token has expired');
        return null;
      }
    }
    
    return decoded;
  } catch (error) {
    console.error('Error verifying token:', error);
    return null;
  }
};

/**
 * Checks if a token is a magic link token and returns its expiration status
 * @param {String} token - Token to check
 * @returns {Object} Object with isMagicLink and isExpired flags
 */
export const checkMagicLinkStatus = (token) => {
  if (!token) {
    return { isMagicLink: false, isExpired: true, remainingTime: 0 };
  }
  
  try {
    const decoded = decodeToken(token);
    
    if (!decoded) {
      return { isMagicLink: false, isExpired: true, remainingTime: 0 };
    }
    
    const isMagicLink = decoded.isMagicLink === true;
    const currentTime = Date.now();
    const isExpired = decoded.exp < currentTime;
    const remainingTime = Math.max(0, decoded.exp - currentTime);
    
    return { 
      isMagicLink, 
      isExpired, 
      remainingTime,
      expiresAt: decoded.exp
    };
  } catch (error) {
    console.error('Error checking magic link status:', error);
    return { isMagicLink: false, isExpired: true, remainingTime: 0 };
  }
};

/**
 * Guarda el token JWT en localStorage
 * @param {String} token - Token JWT a guardar
 */
export const saveToken = (token) => {
  if (token) {
    localStorage.setItem(TOKEN_STORAGE_KEY, token);
  }
};

/**
 * Recupera el token JWT de localStorage
 * @returns {String|null} Token JWT o null si no existe
 */
export const getToken = () => {
  return localStorage.getItem(TOKEN_STORAGE_KEY);
};

/**
 * Elimina el token JWT de localStorage
 */
export const removeToken = () => {
  localStorage.removeItem(TOKEN_STORAGE_KEY);
};
